<template>
  <page-template :is-loading="isLoading" :data-exists="!!securityAlerts">
    <template #pageHeadline>
      {{ $t('securityAlert.listPage.pageHeadline') }}
    </template>
    <template #pageContent>
      <rbz-table
        :columns="columns"
        :table-data="securityAlerts"
        :sticky-header="true"
        class="rbz-list-table"
        data-qa="security-alerts-table"
        :row-buttons="rowButtons"
        :new-button-link="getNewSecurityAlert()"
        :show-filter-row="true"
        :new-button-label="$t('securityAlert.listPage.addNew')"
        :show-download-button="false"
        @duplicate-row-button-clicked="duplicateSecurityAlert"
        @delete-row-button-clicked="openDeleteSecurityAlertModal"
      >
      </rbz-table>
      <rbz-modal-dialog
        :show="showDeleteSecurityAlertModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteSecurityAlert"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteSecurityAlertModal = false"
        @closeModal="showDeleteSecurityAlertModal = false"
        size="medium"
      >
        <template #content>
          {{ $t('common.operations.delete.modal.message', { name: selectedSecurityAlert.name }) }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { DocumentName, DynamicRule, SecurityAlert, ServerGroup } from '@/types';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import PageTemplate from '@/views/PageTemplate.vue';
import _ from 'lodash';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'SecurityAlertsList',

  setup() {
    const { getEditPath, getNewPath, navigateTo } = useRouting();
    return {
      getEditPath,
      getNewPath,
      navigateTo,
    };
  },

  components: {
    PageTemplate,
    RbzModalDialog,
    RbzTable,
  },

  data() {
    return {
      columns: [
        {
          id: 'security-alert-name',
          title: this.$t('securityAlert.listPage.name'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          classes: 'clickable',
          autoFocus: true,
          linkCallback: (item) => {
            return this.getSecurityAlertPath(item);
          },
        },
        {
          id: 'server-groups',
          title: this.$t('securityAlert.listPage.serverGroups'),
          fieldNames: ['server_groups'],
          displayFunction: (item: SecurityAlert) => {
            const serverGroups = _.filter(this.serverGroupsNames, (serverGroup) => {
              return item['server_groups'].includes(serverGroup[0]);
            });
            const serverGroupsNames = _.map(serverGroups, (serverGroup) => {
              return serverGroup[1];
            });
            return serverGroupsNames.join(', ');
          },
          isSearchable: true,
          classes: 'width-240px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'dynamic-rules',
          title: this.$t('securityAlert.listPage.dynamicRules'),
          fieldNames: ['dynamic_rules'],
          displayFunction: (item: SecurityAlert) => {
            const dynamicRules = _.filter(this.dynamicRulesNames, (dynamicRule) => {
              return item['dynamic_rules'].includes(dynamicRule[0]);
            });
            const dynamicRulesNames = _.map(dynamicRules, (serverGroup) => {
              return serverGroup[1];
            });
            return dynamicRulesNames.join(', ');
          },
          classes: 'width-310px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'alert-recipients',
          title: this.$t('securityAlert.listPage.recipients'),
          fieldNames: ['recipients'],
          displayFunction: (item: SecurityAlert) => {
            return item['recipients'].join(', ');
          },
          classes: 'width-400px',
          cellContentClasses: 'ellipsis',
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          linkCallback: this.getSecurityAlertPath,
        },
        {
          id: 'duplicate',
          tooltip: this.$t('common.operations.duplicate.tableTooltip'),
          icon: 'IconDuplicate',
        },
        {
          id: 'delete',
          tooltip: this.$t('common.operations.delete.tableTooltip'),
          icon: 'IconDelete',
        },
      ] as RbzTableRowButton[],
      securityAlerts: [],
      pageLoadingCounter: null,

      serverGroupsNames: [] as [ServerGroup['id'], ServerGroup['name']][],
      dynamicRulesNames: [] as [DynamicRule['id'], DynamicRule['name']][],

      showDeleteSecurityAlertModal: false,
      selectedSecurityAlert: null,
    };
  },

  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if (
          (this.$route.name as string).includes('Security Alerts List') &&
          val &&
          val !== oldVal
        ) {
          this.setPageLoadingStatus(true);
          await this.loadSecurityAlerts();
          const serverGroupsPromise = this.loadServerGroups();
          const dynamicRulesPromise = this.loadDynamicRules();
          await Promise.all([serverGroupsPromise, dynamicRulesPromise]);
          this.setPageLoadingStatus(false);
        }
      },
      immediate: true,
    },
  },

  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('securityAlert.plural'),
        singular: this.$t('securityAlert.singular'),
      };
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },
  },

  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    getNewSecurityAlert() {
      return this.getNewPath('security-alerts');
    },

    getSecurityAlertPath(securityAlert: SecurityAlert) {
      return `/${this.selectedBranch}/security-alerts/config/${securityAlert.id}`;
    },

    editSecurityAlert(securityAlert: SecurityAlert) {
      this.navigateTo(this.getSecurityAlertPath(securityAlert));
    },

    async duplicateSecurityAlert(securityAlert: SecurityAlert) {
      this.setPageLoadingStatus(true);
      const getOriginalResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/security-alerts/${securityAlert.id}`,
      });
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      if (!getOriginalResponse?.data) {
        Utils.toast({
          message: failureMessage,
          type: 'is-danger',
        });
        return;
      }
      const originalSecurityAlert = getOriginalResponse.data;
      const securityAlertToAdd = _.cloneDeep(originalSecurityAlert);
      securityAlertToAdd.id = DatasetsUtils.generateUUID2();
      securityAlertToAdd.name = Utils.generateUniqueEntityName(
        securityAlertToAdd.name,
        _.map(this.securityAlerts, 'name'),
        true
      );
      const url = `conf/${this.selectedBranch}/security-alerts/${securityAlertToAdd.id}`;
      const postNewResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: securityAlertToAdd,
        successMessage,
        failureMessage,
      });
      if (postNewResponse) {
        this.editSecurityAlert(securityAlertToAdd);
      }
      this.setPageLoadingStatus(false);
    },

    async deleteSecurityAlert() {
      this.setPageLoadingStatus(true);
      const securityAlertId = this.selectedSecurityAlert.id;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/security-alerts/${securityAlertId}`,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.securityAlerts = _.filter(this.securityAlerts, (securityAlert) => {
          return securityAlert.id !== securityAlertId;
        });
      }
      this.showDeleteSecurityAlertModal = false;
      this.setPageLoadingStatus(false);
    },

    openDeleteSecurityAlertModal(securityAlert: SecurityAlert) {
      this.showDeleteSecurityAlertModal = true;
      this.selectedSecurityAlert = securityAlert;
    },

    async loadSecurityAlerts() {
      this.setPageLoadingStatus(true);
      const url = `conf/${this.selectedBranch}/security-alerts`;
      const fieldNames = _.flatMap(this.columns, 'fieldNames');
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url,
        config: { headers: { 'x-fields': `id, ${_.uniq(fieldNames).join(', ')}` } },
      });
      this.securityAlerts = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    async loadServerGroups() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/server-groups`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.serverGroupsNames = _.sortBy(
        _.map(response?.data?.items, (entity) => {
          return [entity.id, entity.name];
        }),
        (e) => {
          return e[1];
        }
      );
    },

    async loadDynamicRules() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/dynamic-rules`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.dynamicRulesNames = _.sortBy(
        _.map(response?.data?.items, (entity) => {
          return [entity.id, entity.name];
        }),
        (e) => {
          return e[1];
        }
      );
    },
  },
});
</script>
